import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2, HardHat } from "lucide-react";

const MAX_ATTEMPTS = 6;

export default function PaymentReturn({ cancelled = false }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { user, reloadUser } = useAuth();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [kind, setKind] = useState(null);

  const dashPath = user?.role === "monteur" ? "/monteur" : "/opdrachtgever";

  const poll = useCallback(async (attempt = 0) => {
    if (!sessionId) { setState("error"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      setKind(data.kind);
      if (data.payment_status === "paid") {
        await reloadUser();
        setState("success");
        return;
      }
      if (data.status === "expired") { setState("error"); return; }
    } catch (e) {
      console.error("Betaalstatus ophalen mislukt, nieuwe poging volgt:", e?.message || e);
    }
    if (attempt + 1 >= MAX_ATTEMPTS) { setState("error"); return; }
    setTimeout(() => poll(attempt + 1), 2000);
  }, [sessionId, reloadUser]);

  useEffect(() => {
    if (!cancelled) poll(0);
  }, [cancelled, poll]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div data-testid="payment-return" className="w-full max-w-md bg-white border border-slate-200 rounded-md p-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-8 h-8 bg-orange-500 flex items-center justify-center rounded-sm">
            <HardHat className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-extrabold">Work4you</span>
        </div>

        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
            <h1 className="font-display text-2xl font-extrabold text-slate-950 mb-2">Betaling controleren...</h1>
            <p className="text-slate-500 text-sm">Een moment geduld terwijl we je betaling bevestigen.</p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-green-600 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-extrabold text-slate-950 mb-2">Betaling gelukt!</h1>
            <p className="text-slate-600 text-sm mb-6">
              {kind === "pro"
                ? "Je Pro-toegang is met 3 maanden verlengd. Je kunt nu alle functies gebruiken."
                : "Je opdracht staat nu live en is zichtbaar voor professionals."}
            </p>
            <Button data-testid="payment-continue-btn" onClick={() => navigate(dashPath)}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-sm font-bold h-11">
              Naar mijn dashboard
            </Button>
          </>
        )}

        {state === "cancelled" && (
          <>
            <XCircle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-extrabold text-slate-950 mb-2">Betaling geannuleerd</h1>
            <p className="text-slate-600 text-sm mb-6">Je betaling is niet voltooid. Je kunt het opnieuw proberen vanuit je dashboard.</p>
            <Button data-testid="payment-back-btn" onClick={() => navigate(dashPath)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold h-11">
              Terug naar dashboard
            </Button>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-500 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-extrabold text-slate-950 mb-2">Status onbekend</h1>
            <p className="text-slate-600 text-sm mb-6">We konden je betaalstatus niet bevestigen. Controleer je dashboard of probeer het later opnieuw.</p>
            <Button data-testid="payment-back-btn" onClick={() => navigate(dashPath)}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-sm font-bold h-11">
              Terug naar dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
